<script setup>
import MegaButton from "@components/MegaButton.vue";
import HeroGameCarousel from "@components/HeroGameCarousel.vue";
const innerLabels = [
    {
        text: "Players",
        class: "text-black right-[67px] top-[-30px] rotate-[-15deg] bg-white md:right-[77%] md:top-[256px]",
    },
    {
        text: "Creators",
        class: "text-black right-0 top-[17px] rotate-[22.22deg] bg-accent md:right-[37%] md:top-[43px]",
    },
];

const outerLabels = [
    {
        text: "Games",
        class: "text-black right-[116px] top-[120px] z-10 rotate-[30.31deg] bg-accent-dark md:right-[50%] md:top-[387px] md:rotate-[-17.7deg]",
    },
    {
        text: "Developers",
        class: "text-primary right-[-74px] top-[86px] z-10 rotate-[-17.7deg] bg-black md:right-[48%] md:top-[181px]",
    },
];
</script>

<template>
    <section id="hero" class="w-full !p-[8px] xl:h-[800px]">
        <!-- Inner yellow container -->
        <div
            class="relative isolate flex h-full w-full flex-col items-center justify-between rounded-[24px] bg-primary px-[16px] pt-[112px] pb-[16px] md:justify-start md:gap-[8px] md:px-[48px] md:pt-[136px] md:pb-[32px]"
        >
            <!-- Decorative ribbon SVG -->
            <img
                src="https://wishlisted.s3.amazonaws.com/images/ribbons/ribbon-hero.svg"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute z-[1] right-0 bottom-[-331px] h-[1222px] w-[1920px] object-cover"
            />

            <div class="container flex-1">
                <!-- Layout: flex-col on mobile, 2-column grid on desktop -->
                <div
                    class="relative z-[4] flex h-full w-full flex-col gap-[24px] xl:grid xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] xl:gap-x-[48px]"
                >
                    <!-- LEFT COLUMN -->
                    <div
                        class="relative flex flex-col items-start justify-between gap-4"
                    >
                        <!-- group-text -->
                        <div
                            class="relative flex w-full flex-col items-start gap-[16px] md:gap-[32px]"
                        >
                            <!-- Section label -->
                            <div class="flex items-center gap-[8px]">
                                <img
                                    src="https://wishlisted.s3.amazonaws.com/images/decorative/diamond.svg"
                                    alt=""
                                    class="h-3 w-3"
                                />
                                <span
                                    class="font-obviously font-bold text-[12px] uppercase leading-[1.4] tracking-[-0.12px] text-black"
                                >
                                    What is it
                                </span>
                            </div>

                            <!-- Content: title + infos -->
                            <div
                                class="flex w-full flex-col items-start gap-[24px] pb-[16px] md:gap-[48px] md:pb-0"
                            >
                                <!-- Title -->
                                <div
                                    class="flex flex-col items-start md:gap-[12px]"
                                >
                                    <h1
                                        class="font-obviously font-bold text-[44px] leading-none text-black md:text-[96px]"
                                    >
                                        We Are
                                    </h1>
                                    <div class="flex items-center gap-[8px]">
                                        <img
                                            src="https://wishlisted.s3.amazonaws.com/images/decorative/union-w.svg"
                                            alt=""
                                            class="self-stretch w-auto md:w-full max-w-[44px]"
                                        />
                                        <span
                                            class="font-obviously font-bold text-[44px] leading-none text-black md:text-[96px]"
                                        >
                                            Wishlisted
                                        </span>
                                    </div>
                                </div>

                                <!-- Infos: description + event bar -->
                                <div
                                    class="flex w-full flex-col items-start gap-[16px] md:gap-[40px]"
                                >
                                    <!-- Description -->
                                    <p
                                        class="w-full font-obviously text-[10px] leading-[1.5] tracking-[-0.1px] text-black md:text-[14px] md:tracking-[-0.14px]"
                                    >
                                        We're bridging the gap between visionary
                                        indie developers and the players who
                                        love them. With over 100+ games
                                        showcased, you're sure to find a title
                                        you love. Tune into Wishlisted to
                                        discover the games you didn't know you
                                        were missing.
                                    </p>

                                    <!-- Event info bar (rotated on mobile) -->
                                    <div
                                        class="w-fit rotate-[-1.53deg] md:rotate-0"
                                    >
                                        <div class="bg-black p-[12px]">
                                            <p
                                                class="font-obviously text-[14px] leading-[1.5] tracking-[-0.14px] text-primary md:text-[16px] md:tracking-[-0.16px]"
                                            >
                                                <span
                                                    class="font-obviously font-bold"
                                                    >Watch live April 24th, 2026
                                                </span>
                                                <span
                                                    >on Twitch and
                                                    Youtube.</span
                                                >
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Floating labels (absolute within group-text) -->
                            <span
                                v-for="label in innerLabels"
                                :key="label.text"
                                class="absolute p-[8px] font-obviously-condensed font-bold text-[22px] uppercase leading-none"
                                :class="label.class"
                            >
                                {{ label.text }}
                            </span>
                        </div>

                        <!-- Desktop buttons (xl only, inside left column for grid) -->
                        <div
                            class="hidden w-full items-center gap-[12px] xl:flex"
                        >
                            <!-- <MegaButton
                                href="https://forms.gle/5Sf4He7CEVWMhYcCA"
                                label="Apply now"
                                sublabel="Applications open until 3/20"
                            /> -->
                            <MegaButton
                                href="https://www.youtube.com/@wishlistedgg?sub_confirmation=1"
                                label="See you on April 24th!"
                            />
                            <MegaButton
                                href="#why"
                                label="Learn More"
                                variant="secondary"
                            />
                        </div>
                    </div>

                    <!-- Game carousel -->
                    <HeroGameCarousel />

                    <!-- Tablet buttons (md–xl, below carousel in stack) -->
                    <div
                        class="hidden w-full items-center gap-[12px] md:flex xl:hidden"
                    >
                        <!-- <MegaButton
                            href="https://forms.gle/5Sf4He7CEVWMhYcCA"
                            label="Apply now"
                            sublabel="Applications open until 3/20"
                        /> -->
                        <MegaButton
                            href="https://www.youtube.com/@wishlistedgg?sub_confirmation=1"
                            label="See you on April 24th!"
                        />
                        <MegaButton
                            href="#why"
                            label="Learn More"
                            variant="secondary"
                        />
                    </div>
                </div>

                <!-- Mobile buttons -->
                <div
                    class="z-[2] flex w-full items-center gap-[8px] sm:mt-5 mt-3 md:hidden"
                >
                    <!-- <MegaButton
                        href="https://forms.gle/5Sf4He7CEVWMhYcCA"
                        label="Apply now"
                        sublabel="Applications open until 3/20"
                        class="flex-1"
                    /> -->
                    <MegaButton
                        href="https://www.youtube.com/@wishlistedgg?sub_confirmation=1"
                        label="See you on April 24th!"
                        class="flex-1"
                    />
                    <MegaButton
                        href="#why"
                        label="Learn More"
                        variant="secondary"
                        class="flex-1"
                    />
                </div>
            </div>

            <!-- SCROLL DOWN indicator (desktop only) -->
            <div
                class="hidden h-[40px] items-center gap-[8px] self-end xl:flex"
            >
                <img
                    src="https://wishlisted.s3.amazonaws.com/images/arrows/arrow-right-scroll.svg"
                    alt=""
                    class="h-5 w-5 rotate-90"
                />
                <span
                    class="font-obviously text-[12px] uppercase leading-[1.4] tracking-[-0.12px] text-black"
                >
                    scroll down
                </span>
            </div>

            <!-- Floating labels: Games & Developers -->
            <span
                v-for="label in outerLabels"
                :key="label.text"
                class="absolute p-[8px] font-obviously-condensed font-bold text-[22px] uppercase leading-none"
                :class="label.class"
            >
                {{ label.text }}
            </span>
        </div>
    </section>
</template>
